/**
 * Drag something by a handle: how far it has been moved from where it opened. Pointer Events, so one path
 * covers mouse, pen and touch; the handle takes `touch-action: none` so a finger on it drags rather than
 * scrolls. A double-click (or double-tap) on the handle puts it back.
 */
import { useCallback, useRef, useState } from 'react';
import type React from 'react';

export interface DragOffset { dx: number; dy: number }

export function useDragOffset() {
  const [offset, setOffset] = useState<DragOffset>({ dx: 0, dy: 0 });
  const [dragging, setDragging] = useState(false);
  const from = useRef<{ x: number; y: number; dx: number; dy: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    from.current = { x: e.clientX, y: e.clientY, ...offset };
    setDragging(true);
  }, [offset]);
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const f = from.current;
    // The pointer is kept on screen, and the handle moves with it, so the pop-up can never be dragged out of reach.
    const x = Math.max(0, Math.min(e.clientX, window.innerWidth));
    const y = Math.max(0, Math.min(e.clientY, window.innerHeight));
    if (f) setOffset({ dx: f.dx + x - f.x, dy: f.dy + y - f.y });
  }, []);
  const end = useCallback(() => { from.current = null; setDragging(false); }, []);
  const reset = useCallback(() => setOffset({ dx: 0, dy: 0 }), []);

  return {
    offset,
    dragging,
    reset,
    handleProps: {
      onPointerDown, onPointerMove, onPointerUp: end, onPointerCancel: end, onDoubleClick: reset,
      style: { cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none' as const, userSelect: 'none' as const },
    },
  };
}
